// Product edit modal (PR #152, 2026-05-26). Operator-locked: "clicking
// anywhere on the product opens a popup on that product to: change
// images, add images, change pricing, change descriptions, add imprint
// locations, etc."
//
// MVP scope:
//   - Tabs: Description / Pricing / Charges / Imprint
//   - Reuses existing components (TierTable, ItemChargesSection,
//     DecorationSection) so behavior + validation stays consistent
//     with the rest of the app.
//   - Image / additional-image editing: future PR (no schema for
//     per-presentation-item image overrides yet).
//
// The modal is presentation-only — mutations still go through the
// existing PATCH /presentation_items/:id, /presentation_items/:id/tiers,
// etc. routes that the embedded components own.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ApiError, api } from "@/lib/api";
import { friendlyApiError } from "@/lib/apiErrorCopy";
import { cn } from "@/lib/utils";
import { ProductEditModalShell } from "@/components/product-edit/ProductEditModalShell";
import {
  type PresentationItem,
  TierTable,
} from "@/pages/PresentationDetailPage";
import { DecorationSection } from "@/pages/PresentationDecorationSection";
import { ItemChargesSection } from "@/pages/PresentationItemChargesSection";

interface Props {
  item: PresentationItem;
  /** Other items in the same presentation — used by DecorationSection's
   *  "Copy to other items" picker. */
  siblingItems: Array<{ id: string; name: string }>;
  canEdit: boolean;
  /** Fired after any mutation succeeds; parent should refetch the
   *  presentation detail so the modal's view re-renders with fresh
   *  data on the next open. */
  onMutated: () => void;
  onClose: () => void;
}


/** Snapshot imageUrls arrive as an ordered array OR (legacy) an object
 *  keyed by variant. Normalize to an ordered string array; index 0 is the
 *  primary image both the rep + public UIs render. */
function normalizeImages(imageUrls: unknown): string[] {
  if (Array.isArray(imageUrls)) {
    return imageUrls.filter((u): u is string => typeof u === "string");
  }
  if (imageUrls && typeof imageUrls === "object") {
    return Object.values(imageUrls as Record<string, unknown>).filter(
      (u): u is string => typeof u === "string",
    );
  }
  return [];
}

function maxTierQty(tiers: PresentationItem["tiers"]): number {
  let max = 1;
  for (const t of tiers) {
    if (t.quantity > max) max = t.quantity;
  }
  return max;
}

/** Every tier quantity, ascending and de-duplicated. "Price from rate card"
 *  prices decoration AT one of these rather than at a guessed quantity. */
function tierQuantities(tiers: PresentationItem["tiers"]): number[] {
  return [...new Set(tiers.map((t) => t.quantity))].sort((a, b) => a - b);
}

export function ProductEditModal(props: Props) {
  const { item, siblingItems, canEdit, onMutated, onClose } = props;

  const snapshot = item.productSnapshot ?? {};
  const name = snapshot.name ?? "Untitled product";
  const imageUrls = snapshot.imageUrls;
  const imageUrl = Array.isArray(imageUrls)
    ? imageUrls[0]
    : imageUrls && typeof imageUrls === "object"
      ? (Object.values(imageUrls)[0] as string | undefined)
      : undefined;

  const decorationCount = item.decorations?.length ?? 0;
  const chargeCount = item.itemCharges?.length ?? 0;

  // 🔴 ONE SCREEN, NOT FIVE TABS. CEO 2026-08-18:
  // "You should be able to edit an item and everything is in one place -
  //  rather than going tab by tab and clicking an 'edit' button every time a
  //  pricing change is needed."
  //
  // The five tabs (Description / Images / Pricing / Charges / Imprint) are now
  // five stacked SECTIONS in one scrolling body. Nothing about the sections
  // themselves changed — same components, same props, same endpoints — so this
  // is a layout change, not a rewrite of the money or decoration logic.
  //
  // It also closes the GM's note that imprint charges sit in a separate tab
  // from the imprint: they are now visibly adjacent.
  //
  // ⚠️ The shell still takes a `tabs` list and the estimate / sales-order line
  // editor still passes one. Handing over an EMPTY list is what suppresses the
  // strip, so the other surface keeps its tabs untouched.
  const tabs: never[] = [];

  return (
    <ProductEditModalShell
      ariaLabel={`Edit ${name}`}
      imageUrl={imageUrl}
      title={name}
      subtitle={
        <>
          {snapshot.sku ? `SKU ${snapshot.sku} · ` : ""}
          {item.tiers.length} tier{item.tiers.length === 1 ? "" : "s"} ·{" "}
          {chargeCount} charge{chargeCount === 1 ? "" : "s"} ·{" "}
          {decorationCount} imprint
          {decorationCount === 1 ? "" : "s"}
        </>
      }
      tabs={tabs}
      onClose={onClose}
    >
      <div className="flex flex-col gap-8">
        <EditSection title="Description">
          <DescriptionTab item={item} canEdit={canEdit} onMutated={onMutated} />
        </EditSection>

        <EditSection title="Pricing" hint="the supplier's own quantity breaks">
          <TierTable
            itemId={item.id}
            tiers={item.tiers}
            canEdit={canEdit}
            onMutated={onMutated}
          />
        </EditSection>

        <EditSection title="Charges" count={chargeCount}>
          <ItemChargesSection
            itemId={item.id}
            itemMaxTierQty={maxTierQty(item.tiers)}
            itemTierQuantities={tierQuantities(item.tiers)}
            charges={item.itemCharges ?? []}
            canEdit={canEdit}
            onMutated={onMutated}
          />
        </EditSection>

        <EditSection title="Imprint" count={decorationCount}>
          <DecorationSection
            itemId={item.id}
            itemMaxTierQty={maxTierQty(item.tiers)}
            decorations={item.decorations ?? []}
            siblingItems={siblingItems}
            canEdit={canEdit}
            onMutated={onMutated}
          />
        </EditSection>

        <EditSection title="Images">
          <ImagesTab
            itemId={item.id}
            productId={item.productId}
            snapshotImageUrls={snapshot.imageUrls}
            canEdit={canEdit}
            onMutated={onMutated}
          />
        </EditSection>
      </div>
    </ProductEditModalShell>
  );
}

/** One labelled band in the single-screen editor.
 *
 *  Sections are ordered by how often a rep touches them, not by how the data
 *  is modelled: description, then pricing, then the charges and imprint that
 *  hang off it, then images last. The heading carries the count where one
 *  exists so a rep can see there ARE charges without scrolling to them —
 *  the job the tab badge used to do. */
function EditSection(props: {
  title: string;
  hint?: string;
  count?: number;
  children: React.ReactNode;
}) {
  const { title, hint, count, children } = props;
  return (
    <section>
      <div className="mb-3 flex items-baseline gap-2 border-b border-brown-50 pb-1.5">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-brown-400">
          {title}
        </h3>
        {count !== undefined && count > 0 && (
          <span className="rounded-full bg-cream-100 px-2 py-0.5 text-[11px] font-semibold text-brown-400">
            {count}
          </span>
        )}
        {hint !== undefined && (
          <span className="text-xs text-brown-300">{hint}</span>
        )}
      </div>
      {children}
    </section>
  );
}

function DescriptionTab(props: {
  item: PresentationItem;
  canEdit: boolean;
  onMutated: () => void;
}) {
  const { item, canEdit, onMutated } = props;
  const snapshot = item.productSnapshot ?? {};
  const effective = item.descriptionOverride ?? snapshot.description ?? "";
  const [draft, setDraft] = useState(effective);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (next: string | null) =>
      api(`/presentation_items/${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({ description_override: next }),
      }),
    onError: (err) =>
      setError(err instanceof Error ? err.message : "Couldn't save."),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  const dirty = draft.trim() !== effective.trim();

  return (
    <div className="space-y-4">
      <div>
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Client-facing description
          </span>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            maxLength={8000}
            disabled={!canEdit || save.isPending}
            placeholder="What should the client see for this product?"
            className="w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm leading-relaxed text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200 disabled:bg-cream-50"
          />
        </label>
        {item.descriptionOverride !== null && canEdit && (
          <p className="mt-2 text-xs text-brown-300">
            Custom description set. The supplier default would otherwise be
            shown.
          </p>
        )}
      </div>
      {error && <p className="text-sm text-rust-600">{error}</p>}
      {canEdit && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate(draft.trim().length === 0 ? null : draft.trim())
            }
          >
            {save.isPending ? "Saving…" : "Save description"}
          </Button>
          {item.descriptionOverride !== null && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={save.isPending}
              onClick={() => {
                save.mutate(null);
                setDraft(snapshot.description ?? "");
              }}
            >
              Reset to supplier default
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Images tab (2026-07-23) ───────────────────────────────────────────
// CEO: "there should be an option to upload images (mockup or otherwise)
// that would replace the image in the presentation." Snapshot-scoped —
// writes affect this presentation item only, never the catalog product.
//
// Upload reuses the existing R2 presigned-PUT flow (mirrors the decoration
// LogoUploader): POST image-upload → PUT bytes → PATCH images with the new
// public URL as index 0 (primary). Set-primary reorders; remove deletes.
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

/** GET /products/:id/variant-images response — one entry per variant color.
 *  `mirrored_url` is the R2 copy when the color was already selected once;
 *  primary detection must check BOTH urls (post-mirror the snapshot holds the
 *  R2 url, pre-mirror a Phase-1-seeded snapshot holds the supplier url). */
interface VariantColorsResponse {
  colors: Array<{
    color: string;
    image_url: string;
    mirrored_url: string | null;
  }>;
}

function selectColorErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: string } | null)?.error;
    const friendly = friendlyApiError(code);
    if (friendly) return friendly;
  }
  return err instanceof Error ? err.message : "Couldn't set that color.";
}

function ImagesTab(props: {
  itemId: string;
  productId: string;
  snapshotImageUrls: unknown;
  canEdit: boolean;
  onMutated: () => void;
}) {
  const { itemId, productId, snapshotImageUrls, canEdit, onMutated } = props;
  const [images, setImages] = useState<string[]>(() =>
    normalizeImages(snapshotImageUrls),
  );
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Per-color image gallery from the product's variants (supplier data).
  // Failure or an empty result hides the section — it's an enhancement on
  // top of the snapshot grid, and many products have no variant colors.
  const colorsQuery = useQuery({
    queryKey: ["product-variant-images", productId],
    queryFn: () =>
      api<VariantColorsResponse>(`/products/${productId}/variant-images`),
    staleTime: 5 * 60 * 1000,
  });
  const colors = colorsQuery.data?.colors ?? [];

  // A failed gallery fetch degrades to "no section", indistinguishable from a
  // colorless product in the UI — leave a debugging trail (UI-review note 6).
  useEffect(() => {
    if (colorsQuery.error) {
      console.warn("[variant-images] gallery fetch failed", colorsQuery.error);
    }
  }, [colorsQuery.error]);

  // Selecting a color mirrors its photo to our own storage server-side and
  // makes it the primary image. pendingColor doubles as a synchronous
  // double-click guard (same recipe as the presentation add-product button).
  const [pendingColor, setPendingColor] = useState<string | null>(null);
  const selectColor = useMutation({
    mutationFn: async (color: string) =>
      api<{ item: { productSnapshot?: { imageUrls?: unknown } } }>(
        `/presentation_items/${itemId}/images/select-color`,
        { method: "POST", body: JSON.stringify({ color }) },
      ),
    onError: (err) => {
      setPendingColor(null);
      setError(selectColorErrorCopy(err));
    },
    onSuccess: (data) => {
      setPendingColor(null);
      setError(null);
      setImages(normalizeImages(data.item.productSnapshot?.imageUrls));
      // The picked color now has a mirrored_url — refresh the gallery so
      // primary detection tracks the rewritten snapshot url.
      void queryClient.invalidateQueries({
        queryKey: ["product-variant-images", productId],
      });
      onMutated();
    },
  });

  const onSelectColor = (color: string): void => {
    if (pendingColor !== null || selectColor.isPending) return;
    setPendingColor(color);
    setError(null);
    selectColor.mutate(color);
  };

  // Re-seed when the modal is reopened on a different item / after a
  // parent refetch hands down fresh props.
  useEffect(() => {
    setImages(normalizeImages(snapshotImageUrls));
  }, [snapshotImageUrls]);

  // Single writer: PATCH the full ordered array. index 0 = primary.
  const save = useMutation({
    mutationFn: async (next: string[]) => {
      await api(`/presentation_items/${itemId}/images`, {
        method: "PATCH",
        body: JSON.stringify({ image_urls: next }),
      });
      return next;
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : "Couldn't save images."),
    onSuccess: (next) => {
      setError(null);
      setImages(next);
      onMutated();
    },
  });

  // Upload → then prepend the new public URL as the primary image.
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const presign = await api<{
        upload_url: string;
        public_url: string | null;
        live: boolean;
      }>(`/presentation_items/${itemId}/image-upload`, {
        method: "POST",
        body: JSON.stringify({
          filename: file.name,
          mime_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        }),
      });
      if (presign.live) {
        const put = await fetch(presign.upload_url, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!put.ok) {
          throw new Error(`Upload failed: ${put.status} ${put.statusText}`);
        }
      }
      if (!presign.public_url) {
        throw new Error(
          "Uploaded, but no public URL is configured for this environment.",
        );
      }
      return presign.public_url;
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : "Upload failed."),
    onSuccess: (publicUrl) => {
      // New image becomes the primary (index 0), keeping the rest.
      save.mutate([publicUrl, ...images]);
      if (inputRef.current) inputRef.current.value = "";
    },
  });

  const busy = upload.isPending || save.isPending || selectColor.isPending;

  // A color is the PRIMARY color when its photo — mirrored copy or supplier
  // original — is the current primary image. One vocabulary tab-wide: the
  // badge here and the badge on the snapshot grid both say "Primary".
  const primaryUrl = images[0];
  const isPrimaryColor = (c: VariantColorsResponse["colors"][number]): boolean =>
    primaryUrl !== undefined &&
    (primaryUrl === c.mirrored_url || primaryUrl === c.image_url);

  const onPick = (): void => {
    setError(null);
    inputRef.current?.click();
  };

  const onFile = (file: File): void => {
    setError(null);
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError("Please choose a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("Image is larger than 15 MB.");
      return;
    }
    upload.mutate(file);
  };

  const setPrimary = (index: number): void => {
    if (index === 0) return;
    const target = images[index];
    if (target === undefined) return;
    const next = [target, ...images.filter((_, i) => i !== index)];
    save.mutate(next);
  };

  const remove = (index: number): void => {
    save.mutate(images.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />

      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Presentation images
        </p>
      {images.length === 0 ? (
        <p className="text-sm italic text-brown-300">
          No image on this product yet.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {images.map((url, i) => (
            <div
              key={`${url}-${i}`}
              className={cn(
                "group relative overflow-hidden rounded-md border bg-cream-100",
                i === 0 ? "border-ink ring-1 ring-ink" : "border-brown-50",
              )}
            >
              <div className="aspect-square">
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              {i === 0 && (
                <span className="absolute left-1 top-1 rounded-full bg-ink px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                  Primary
                </span>
              )}
              {canEdit && (
                <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1 bg-brown-500/50 px-1 py-1 opacity-0 transition-opacity group-hover:opacity-100">
                  {i !== 0 && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setPrimary(i)}
                      title="Make primary"
                      className="rounded p-1 text-white hover:bg-white/20 disabled:opacity-50"
                    >
                      <Star className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => remove(i)}
                    title="Remove image"
                    className="rounded p-1 text-white hover:bg-white/20 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </div>

      {colorsQuery.isPending && (
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Product colors
          </p>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div
                key={i}
                className="aspect-square animate-pulse rounded-md border border-brown-50 bg-cream-100"
              />
            ))}
          </div>
        </div>
      )}

      {colors.length > 0 && (
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Product colors
          </p>
          <p className="mb-2 text-[11px] text-brown-300">
            {canEdit
              ? "Pick a color — its photo becomes the primary image on this presentation."
              : "Colors available on this product."}
          </p>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {colors.map((c) => {
              const isPrimary = isPrimaryColor(c);
              const isPendingThis = pendingColor === c.color;
              return (
                <button
                  key={c.color}
                  type="button"
                  disabled={!canEdit || busy || isPrimary}
                  onClick={() => onSelectColor(c.color)}
                  title={
                    isPrimary
                      ? `${c.color} is the primary image`
                      : canEdit
                        ? `Make ${c.color} primary`
                        : c.color
                  }
                  className={cn(
                    "group relative overflow-hidden rounded-md border bg-cream-100 text-left disabled:cursor-default",
                    isPrimary
                      ? "border-ink ring-1 ring-ink"
                      : "border-brown-50",
                    canEdit && !isPrimary && !busy
                      ? "hover:border-peach-400 hover:ring-1 hover:ring-peach-300"
                      : "",
                    // Visibly disabled — matches the file's disabled idiom —
                    // WITHOUT dimming the primary card (its rust ring is a
                    // highlight, not an inactive state).
                    (busy && !isPendingThis) || (!canEdit && !isPrimary)
                      ? "opacity-60"
                      : "",
                  )}
                >
                  <div className="aspect-square">
                    <img
                      src={c.mirrored_url ?? c.image_url}
                      alt={c.color}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  {isPrimary && (
                    <span className="absolute left-1 top-1 rounded-full bg-ink px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                      Primary
                    </span>
                  )}
                  <div className="flex items-center justify-between gap-1 px-1.5 py-1">
                    <span className="truncate text-[10px] font-medium text-brown-400">
                      {c.color}
                    </span>
                    {isPendingThis && (
                      <span className="shrink-0 text-[9px] text-brown-300">
                        Working…
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {canEdit && (
        <button
          type="button"
          onClick={onPick}
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-peach-300 bg-cream-50/40 px-3 py-3 text-xs font-semibold text-brown-400 hover:border-peach-400 hover:bg-cream-50 disabled:opacity-50"
        >
          <ImagePlus className="h-4 w-4" />
          {upload.isPending || save.isPending
            ? "Working…"
            : "Upload image (JPG, PNG, WebP — becomes primary)"}
        </button>
      )}

      {error && <p className="text-xs text-rust-600">{error}</p>}
      {canEdit && (
        <p className="text-[11px] text-brown-300">
          Uploads replace only this item&rsquo;s image in the presentation —
          the catalog product is unchanged.
        </p>
      )}
    </div>
  );
}
